import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/formatError";
import { Crown, Check, Zap, Sparkles, FileText, Mic2, Search } from "lucide-react";

const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

const FREE_FEATURES = [
  "3 job searches total (free trial)",
  "2 AI resume tailorings / day",
  "2 cover letters / day",
  "1 mock interview / day",
];

const PREMIUM_FEATURES = [
  { icon: Search, t: "Unlimited job searches (LinkedIn, Indeed, Glassdoor, Naukri)" },
  { icon: Sparkles, t: "Unlimited AI resume tailoring" },
  { icon: FileText, t: "Unlimited cover letters" },
  { icon: Mic2, t: "Unlimited mock interviews" },
  { icon: Zap, t: "Auto-Apply queue unlocked" },
];

const FREE_MODE = process.env.REACT_APP_FREE_MODE === "true";

export default function Subscribe() {
  const { user, checkAuth } = useAuth();
  const [config, setConfig] = useState(null);
  const [err, setErr] = useState("");
  const [paying, setPaying] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    api.get("/billing/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const subscribe = async () => {
    setErr("");
    setPaying(true);
    try {
      const ok = await loadRazorpay();
      if (!ok) throw new Error("Could not load Razorpay checkout. Check your connection.");
      const { data: order } = await api.post("/billing/create-order");
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "Curcompass",
        description: "Premium Plan — monthly",
        order_id: order.order_id,
        prefill: { name: user?.name || "", email: user?.email || "" },
        theme: { color: "#8b5cf6" },
        handler: async (resp) => {
          try {
            await api.post("/billing/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            setSuccess(true);
            await checkAuth();
          } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || "Payment verification failed");
          } finally {
            setPaying(false);
          }
        },
        modal: { ondismiss: () => setPaying(false) },
      });
      rzp.open();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setPaying(false);
    }
  };

  const premium = config?.is_premium || success;

  return (
    <div className="max-w-4xl mx-auto" data-testid="subscribe-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white flex items-center gap-3">
          <Crown className="w-7 h-7 text-amber-400" /> Go Premium
        </h1>
        <p className="text-zinc-400 mt-1">Unlock your full AI agent team for serious job hunting.</p>
      </div>

      {FREE_MODE && (
        <div className="jr-card mb-6 border border-emerald-500/40 text-emerald-300" data-testid="free-mode-banner">
          🌍 Global launch special — <strong>everything is FREE right now</strong>, no limits. Premium billing will arrive later.
        </div>
      )}

      {success && (
        <div className="jr-card mb-6 border border-emerald-500/40 text-emerald-300" data-testid="subscribe-success">
          🎉 You're Premium now! All limits removed — happy hunting.
        </div>
      )}
      {err && <div className="jr-card mb-6 border border-rose-500/40 text-rose-300" data-testid="subscribe-error">{err}</div>}
      {config && !config.configured && (
        <div className="jr-card mb-6 border border-amber-500/40 text-amber-300" data-testid="billing-not-configured">
          Payments are not configured yet. Please check back soon.
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        {/* Free */}
        <div className="jr-card" data-testid="free-plan-card">
          <div className="text-zinc-300 font-semibold mb-1">Free</div>
          <div className="text-3xl font-bold text-white mb-4">₹0<span className="text-sm text-zinc-400 font-normal">/month</span></div>
          <ul className="space-y-2 text-sm text-zinc-400">
            {FREE_FEATURES.map((f) => (
              <li key={f} className="flex items-center gap-2"><Check className="w-4 h-4 text-zinc-500" />{f}</li>
            ))}
            <li className="flex items-center gap-2 text-zinc-600"><Check className="w-4 h-4" />Auto-Apply locked</li>
          </ul>
          <div className="mt-6 text-center text-xs text-zinc-500 py-2 rounded-xl bg-white/5">
            {premium ? "Previous plan" : "Your current plan"}
          </div>
        </div>

        {/* Premium */}
        <div className="jr-card relative overflow-hidden border border-violet-500/40" data-testid="premium-plan-card"
          style={{ background: "linear-gradient(135deg, rgba(139,92,246,0.15), rgba(34,211,238,0.06))" }}>
          <div className="absolute top-3 right-3 text-[10px] font-bold tracking-widest text-amber-300 bg-amber-400/10 border border-amber-400/30 rounded-full px-2 py-0.5">
            MOST POPULAR
          </div>
          <div className="text-violet-200 font-semibold mb-1 flex items-center gap-2"><Crown className="w-4 h-4 text-amber-400" />Premium</div>
          <div className="text-3xl font-bold text-white mb-4">₹{config?.price_inr ?? 399}<span className="text-sm text-zinc-400 font-normal">/month</span></div>
          <ul className="space-y-2 text-sm text-zinc-300">
            {PREMIUM_FEATURES.map((f) => (
              <li key={f.t} className="flex items-center gap-2"><f.icon className="w-4 h-4 text-violet-300" />{f.t}</li>
            ))}
          </ul>
          {premium || FREE_MODE ? (
            <div className="mt-6 text-center text-sm text-emerald-300 py-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/30" data-testid="premium-active-badge">
              {FREE_MODE ? "✓ Free during global launch — all features unlocked" : `✓ Premium active${config?.premium_until ? ` until ${String(config.premium_until).slice(0, 10)}` : ""}`}
            </div>
          ) : (
            <button onClick={subscribe} disabled={paying || (config && !config.configured)}
              className="jr-btn-primary w-full py-3 mt-6" data-testid="subscribe-btn">
              {paying ? "Opening checkout..." : `Subscribe — ₹${config?.price_inr ?? 399}/month`}
            </button>
          )}
          <p className="text-[11px] text-zinc-500 mt-3 text-center">Secure payment via Razorpay · UPI, cards, netbanking &amp; wallets</p>
        </div>
      </div>
    </div>
  );
}
